import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

const steps = [
    { number: '01', icon: 'bi-person-plus', title: 'Create an Account', text: 'Sign up for free in seconds. No credit card required. Individuals, educators, and non-profits get full access at no cost.' },
    { number: '02', icon: 'bi-award', title: 'Design Your Badge', text: 'Use our intuitive wizard to create a badge. Our AI Assist suggests titles, descriptions, keywords, and criteria — so you spend less time writing and more time issuing.' },
    { number: '03', icon: 'bi-send', title: 'Issue to Earners', text: 'Send badges to individuals by email, via your LMS, or through our API. Set expiry dates, evidence requirements, and access controls.' },
    { number: '04', icon: 'bi-share', title: 'Earners Share & Verify', text: 'Recipients add badges to their portfolio and share them on LinkedIn, email signatures, or anywhere else. Any third party can verify authenticity instantly.' },
];

const concepts = [
    { icon: 'bi-award-fill', title: 'Creating Badges', text: "At the core of TC Everywhere is the creation of digital badges. Organizations — schools, online platforms, employers — make unique badges that represent achievements." },
    { icon: 'bi-robot', title: 'Artificial Intelligence', text: 'AI Assist provides valuable suggestions for your badge\'s title, description, keywords, and even potential assessment questions.' },
    { icon: 'bi-trophy', title: 'Earning Badges', text: 'What makes TC Everywhere special is that users earn badges from organizations. When you finish courses or reach goals, you earn badges that say "Well done!"' },
    { icon: 'bi-safe', title: 'Safe Storage', text: 'Once earned, badges are stored securely in your digital portfolio. No more worrying about losing certificates.' },
    { icon: 'bi-patch-check', title: 'Verifiable Credentials', text: 'Every badge from TC Everywhere is cryptographically verified. Employers and schools can be sure your achievements are legitimate.' },
    { icon: 'bi-plug', title: 'Works with Your LMS', text: 'We offer integrations that allow you to seamlessly send achievements earned on TC Everywhere back to your LMS.' },
    { icon: 'bi-globe', title: 'Open Badges Standard', text: 'TC Everywhere follows the Open Badges 2.0+ standard, making all badges portable. You control which badges you share publicly or keep private.' },
    { icon: 'bi-lock', title: 'Public & Private Badges', text: 'When you create a badge you can make it Public or Private. Public badges appear on the Available Badges page. Private badges can only be earned by people you define.' },
];

export default function HowItWorksPage() {
    return (
        <>
            {/* Hero */}
            <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 10, textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h3" fontWeight={700} gutterBottom>How Does It Work?</Typography>
                    <Typography variant="h6" sx={{ opacity: 0.85 }}>
                        From badge creation to verified credential — here's everything you need to know.
                    </Typography>
                </Container>
            </Box>

            {/* Steps */}
            <Box component="section" sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        {steps.map((step) => (
                            <Grid item xs={12} sm={6} lg={3} key={step.number}>
                                <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                                    <CardContent>
                                        <Typography variant="h5" fontWeight={700} sx={{ color: 'primary.main', opacity: 0.3 }}>{step.number}</Typography>
                                        <Box component="i" className={`bi ${step.icon}`} sx={{ fontSize: '2.5rem', color: 'primary.main', my: 2, display: 'block' }} />
                                        <Typography variant="h6" fontWeight={700} gutterBottom>{step.title}</Typography>
                                        <Typography variant="body2" color="text.secondary">{step.text}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Privacy Controls */}
            <Box component="section" sx={{ py: 8, bgcolor: 'background.paper' }}>
                <Container maxWidth="lg">
                    <Grid container alignItems="center" spacing={6}>
                        <Grid item xs={12} lg={5} sx={{ textAlign: 'center' }}>
                            <Box component="i" className="bi bi-shield-lock-fill" sx={{ fontSize: '7rem', color: 'primary.main', opacity: 0.15 }} />
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <Typography variant="h4" fontWeight={700} gutterBottom>You Control Badge Privacy</Typography>
                            <Typography color="text.secondary" paragraph>
                                When you create a TC Everywhere badge you can make it Public or Private — or even a
                                combination of both. Public badges can be discovered and earned by anyone. Private
                                badges are available only to people you choose, protected by access controls you define.
                            </Typography>
                            <Typography color="text.secondary" paragraph>
                                Use <strong>Access</strong> to manage badge privacy and manually issue badges to
                                learners. Use <strong>Metrics</strong> to watch badge performance — views, clicks,
                                and shares — and drill into who has earned each award.
                            </Typography>
                            <Button component={Link} to="/register" variant="contained">Start Issuing Badges</Button>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Key Concepts */}
            <Box component="section" sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>Key Concepts</Typography>
                    <Typography color="text.secondary" textAlign="center" mb={6}>Everything you need to understand the TC Everywhere ecosystem.</Typography>
                    <Grid container spacing={4}>
                        {concepts.map(({ icon, title, text }) => (
                            <Grid item xs={12} sm={6} lg={3} key={title}>
                                <Box display="flex" gap={2}>
                                    <Box sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: 1, p: 1.5, flexShrink: 0, height: 'fit-content' }}>
                                        <Box component="i" className={`bi ${icon}`} sx={{ fontSize: '1.25rem' }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>{title}</Typography>
                                        <Typography variant="body2" color="text.secondary">{text}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* CTA */}
            <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8, textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h4" fontWeight={700} gutterBottom>See it in action</Typography>
                    <Typography variant="subtitle1" sx={{ opacity: 0.85, mb: 4 }}>
                        Create your first badge in under five minutes — no credit card needed.
                    </Typography>
                    <Button component={Link} to="/register" variant="contained" color="secondary" size="large" sx={{ mr: 2, fontWeight: 700 }}>
                        Get Started Free
                    </Button>
                    <Button component={Link} to="/faqs" variant="outlined" size="large" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}>
                        Read the FAQs
                    </Button>
                </Container>
            </Box>
        </>
    );
}
